import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CalendarIcon, Check, Mail, Phone, MapPin } from 'lucide-react';
import { format } from 'date-fns';

const ContactPage = () => {
  const [siteContent, setSiteContent] = useState(null);
  const [date, setDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: '',
    venue: '',
    guest_count: '',
    budget_range: '',
    vision: '',
    how_heard: '',
    message: '',
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/site-content`);
        setSiteContent(res.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select an event date');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        event_date: format(date, 'yyyy-MM-dd'),
      });
      
      setIsSubmitted(true);
      toast.success('Your inquiry has been submitted!');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="contact-success" className="min-h-screen flex items-center justify-center bg-[#F5F5F0] pt-20">
        <div className="text-center p-12 bg-white rounded-2xl shadow-soft max-w-lg mx-4">
          <div className="w-20 h-20 rounded-full bg-[#4A5D23] flex items-center justify-center mx-auto mb-6">
            <Check className="text-white" size={40} />
          </div>
          <h2 className="font-heading text-4xl text-[#2C2C2C] mb-4">Thank You!</h2>
          <p className="text-[#6B7280] mb-8">
            Your inquiry has been received. We'll be in touch within 24-48 hours to discuss your beautiful event.
          </p>
          <Button
            onClick={() => {
              setIsSubmitted(false);
              setFormData({
                name: '', email: '', phone: '', event_type: '', venue: '',
                guest_count: '', budget_range: '', vision: '', how_heard: '', message: '',
              });
              setDate(null);
            }}
            className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-8"
          >
            Submit Another Inquiry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="bg-[#F5F5F0]">
      {/* Hero */}
      <section className="relative h-[50vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-[#4A5D23]/60" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <span className="font-accent text-4xl text-[#D4AF37] mb-4">Get In Touch</span>
          <h1 className="font-heading text-5xl md:text-7xl font-light">Contact Us</h1>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <span className="font-accent text-3xl text-[#D4AF37]">Let's Connect</span>
              <h2 className="font-heading text-3xl text-[#2C2C2C] mt-2 mb-6">
                We'd Love to Hear From You
              </h2>
              <p className="text-[#6B7280] mb-10">
                Ready to start planning your special event? Fill out our inquiry form and we'll be in touch soon.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8DFD0] flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-[#2C2C2C] mb-1">Location</h4>
                    <p className="text-[#6B7280]">
                      {siteContent?.contact_address || 'Nassau, The Bahamas'}
                      <br />Serving All Islands
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8DFD0] flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-[#2C2C2C] mb-1">Phone</h4>
                    <a 
                      href={`tel:${siteContent?.contact_phone || '+1 (242) 555-0123'}`}
                      className="text-[#6B7280] hover:text-[#D4AF37] transition-colors"
                    >
                      {siteContent?.contact_phone || '+1 (242) 555-0123'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8DFD0] flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-[#2C2C2C] mb-1">Email</h4>
                    <a 
                      href={`mailto:${siteContent?.contact_email || 'hello@edweddingeventsandcreations.com'}`}
                      className="text-[#6B7280] hover:text-[#D4AF37] transition-colors"
                    >
                      {siteContent?.contact_email || 'hello@edweddingeventsandcreations.com'}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Inquiry Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-[#F5F5F0] rounded-2xl p-8 md:p-10">
                <h3 className="font-heading text-2xl text-[#2C2C2C] mb-8">Event Inquiry Form</h3>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Full Name *</Label>
                    <Input
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="input-elegant"
                      placeholder="Your name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Email Address *</Label>
                    <Input
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="input-elegant"
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Phone Number *</Label>
                    <Input
                      name="phone"
                      data-testid="contact-phone-input"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="input-elegant"
                      placeholder="+1 (242) 555-0123"
                    />
                  </div>

                  {/* Event Type */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Event Type *</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, event_type: value }))}
                      required
                    >
                      <SelectTrigger data-testid="contact-event-type" className="input-elegant">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Wedding">Wedding</SelectItem>
                        <SelectItem value="Anniversary">Anniversary Celebration</SelectItem>
                        <SelectItem value="Dinner Party">Dinner Party</SelectItem>
                        <SelectItem value="General Party">General Party</SelectItem>
                        <SelectItem value="Engagement Party">Engagement Party</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Event Date */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Event Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="contact-date-picker"
                          className="w-full justify-start text-left font-normal input-elegant"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                          {date ? format(date, 'PPP') : <span className="text-gray-400">Select a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Venue */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Venue (if known)</Label>
                    <Input
                      name="venue"
                      data-testid="contact-venue-input"
                      value={formData.venue}
                      onChange={handleInputChange}
                      className="input-elegant"
                      placeholder="Venue name or location"
                    />
                  </div>

                  {/* Guest Count */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Guest Count</Label>
                    <Select
                      value={formData.guest_count}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, guest_count: value }))}
                    >
                      <SelectTrigger data-testid="contact-guest-count" className="input-elegant">
                        <SelectValue placeholder="Estimated guests" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under 25">Under 25</SelectItem>
                        <SelectItem value="25-50">25-50</SelectItem>
                        <SelectItem value="50-100">50-100</SelectItem>
                        <SelectItem value="100-200">100-200</SelectItem>
                        <SelectItem value="200+">200+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Budget Range */}
                  <div>
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Budget Range</Label>
                    <Select
                      value={formData.budget_range}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, budget_range: value }))}
                    >
                      <SelectTrigger data-testid="contact-budget" className="input-elegant">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under $5,000">Under $5,000</SelectItem>
                        <SelectItem value="$5,000 - $10,000">$5,000 - $10,000</SelectItem>
                        <SelectItem value="$10,000 - $25,000">$10,000 - $25,000</SelectItem>
                        <SelectItem value="$25,000 - $50,000">$25,000 - $50,000</SelectItem>
                        <SelectItem value="$50,000+">$50,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* How Heard */}
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">How did you hear about us?</Label>
                    <Select
                      value={formData.how_heard}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, how_heard: value }))}
                    >
                      <SelectTrigger data-testid="contact-how-heard" className="input-elegant">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Instagram">Instagram</SelectItem>
                        <SelectItem value="Facebook">Facebook</SelectItem>
                        <SelectItem value="Google">Google Search</SelectItem>
                        <SelectItem value="Referral">Friend/Family Referral</SelectItem>
                        <SelectItem value="Vendor">Vendor Referral</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Vision */}
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Tell us about your vision</Label>
                    <Textarea
                      name="vision"
                      data-testid="contact-vision-input"
                      value={formData.vision}
                      onChange={handleInputChange}
                      className="input-elegant min-h-[100px]"
                      placeholder="Describe your dream event - colors, themes, feelings you want to create..."
                    />
                  </div>

                  {/* Additional Message */}
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Additional Details</Label>
                    <Textarea
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="input-elegant min-h-[80px]"
                      placeholder="Any other information you'd like to share..."
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="mt-8">
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full py-6 text-lg"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
